import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Car, Wrench, Settings, Shield, Clock, DollarSign,
  ChevronDown, Phone, MapPin, Calendar, Star,
  ShoppingCart, Zap, Info, FileText
} from 'lucide-react';

const MegaMenu = ({ isScrolled, onBookService }) => {
  const [activeMenu, setActiveMenu] = useState(null);
  const timeoutRef = useRef(null);

  const handleMouseEnter = (menu) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setActiveMenu(menu);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setActiveMenu(null);
    }, 200);
  };

  const megaMenuData = {
    vehicles: {
      title: 'Vehicles',
      sections: [
        {
          heading: 'Browse Inventory',
          icon: Car,
          items: [
            { name: 'All Vehicles', href: '/vehicles', description: 'View our full inventory' },
            { name: 'BMW Inventory', href: '/inventory/bmw', description: 'Shop BMW vehicles' },
            { name: 'Audi Inventory', href: '/inventory/audi', description: 'Shop Audi vehicles' },
            { name: 'VW Inventory', href: '/inventory/volkswagen', description: 'Shop Volkswagen vehicles' },
            { name: 'Golf Carts', href: '/golf-carts', description: 'Electric & gas golf carts' },
            { name: 'Trailers', href: '/trailers', description: 'Utility & cargo trailers' }
          ]
        },
        {
          heading: 'Buying Services',
          icon: ShoppingCart,
          items: [
            { name: 'Get Pre-Qualified', href: '/pre-approval', new: true, description: 'Quick soft-pull pre-approval' },
            { name: 'Parts & Accessories', href: '/parts', hot: true, description: 'OBD scanners, parts & tools' },
            { name: 'Trade-In', href: '/trade-in', description: 'Get your trade value' },
            { name: 'Finance Application', href: '/finance-application', hot: true, description: 'Apply for financing' }
          ]
        },
        {
          heading: 'Featured',
          featured: true,
          content: (
            <div className="bg-gradient-to-br from-primary-50 to-primary-100 p-6 rounded-lg">
              <h4 className="font-bold text-primary-800 mb-2">🚨 Expert Service Available</h4>
              <p className="text-sm text-gray-700 mb-3">
                Professional repair & maintenance for all makes and models!
              </p>
              <Link to="/service" className="inline-block bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700 transition-colors text-sm font-semibold">
                Book Service Now →
              </Link>
            </div>
          )
        }
      ]
    },
    services: {
      title: 'Services',
      sections: [
        {
          heading: 'European Specialists',
          icon: Shield,
          items: [
            { name: 'BMW Repair', href: '/services/european-car-repair/bmw', hot: true, description: 'Factory-trained BMW experts' },
            { name: 'Mercedes Service', href: '/services/european-car-repair/mercedes', description: 'Certified Mercedes techs' },
            { name: 'Audi Service', href: '/services/european-car-repair/audi', description: 'Quattro specialists' },
            { name: 'Porsche Repair', href: '/services/european-car-repair/porsche', description: 'Performance & maintenance' },
            { name: 'Volkswagen', href: '/services/european-car-repair/volkswagen', description: 'VW & TDI experts' },
            { name: 'Jaguar Service', href: '/services/european-car-repair/jaguar', description: 'British luxury specialists' },
            { name: 'Land Rover', href: '/services/european-car-repair/land-rover', description: 'Range Rover experts' },
            { name: 'Volvo Repair', href: '/services/european-car-repair/volvo', description: 'Swedish engineering pros' },
            { name: 'MINI Cooper', href: '/services/european-car-repair/mini-cooper', description: 'MINI specialists' }
          ]
        },
        {
          heading: 'General Services',
          icon: Wrench,
          items: [
            { name: 'Check Engine Light', href: '/services/diagnostic-check-engine-light', hot: true, description: 'Free diagnostic scan' },
            { name: 'Transmission Repair', href: '/services/transmission-repair', description: 'Repair & maintenance' },
            { name: 'Suspension Repair', href: '/services/suspension-repair', description: 'Shocks & struts' },
            { name: 'Vehicle Diagnostics', href: '/services/vehicle-diagnostics', description: 'Computer scanning' },
            { name: 'Radiator Repair', href: '/services/radiator-repair', description: 'Cooling system' },
            { name: 'Factory Maintenance', href: '/services/factory-maintenance', description: 'Scheduled service' },
            { name: 'Wheel & Tire', href: '/services/wheel-and-tire', description: 'Alignment & rotation' },
            { name: 'Timing Belt/Chain', href: '/services/timing-chain-belt-replacement', description: 'Preventive service' },
            { name: 'Clutch Repair', href: '/services/clutch-repair', description: 'Manual transmission' }
          ]
        },
        {
          heading: 'Specialty Services',
          icon: Settings,
          items: [
            { name: 'Golf Cart Service', href: '/golf-cart-services', description: 'Repair & maintenance' },
            { name: 'European Car Repair', href: '/services/european-car-repair', description: 'All European brands' },
          ]
        },
        {
          heading: 'Quick Links',
          featured: true,
          content: (
            <div className="space-y-4">
              <Link to="/service" className="flex items-center justify-between bg-red-50 hover:bg-red-100 p-3 rounded-lg transition-colors group">
                <div>
                  <p className="font-semibold text-red-900">Book Service</p>
                  <p className="text-xs text-red-700">Schedule online 24/7</p>
                </div>
                <Calendar className="w-5 h-5 text-red-600 group-hover:translate-x-1 transition-transform" />
              </Link>
              <div className="bg-gray-100 p-3 rounded-lg">
                <p className="text-xs text-gray-600 mb-1">Emergency Service</p>
                <a href="tel:8173822316" className="font-bold text-gray-900 hover:text-primary-600 transition-colors">
                  (817) 382-2316
                </a>
              </div>
            </div>
          )
        }
      ]
    },
    resources: {
      title: 'Resources',
      sections: [
        {
          heading: 'Tools & Resources',
          icon: Zap,
          items: [
            { name: 'AI Tools', href: '/ai-tools', hot: true, icon: '🤖', description: 'Smart diagnostic tools' },
            { name: 'Blog', href: '/blog', icon: '📝', description: 'Tips & guides' },
            { name: 'Service History', href: '/service', icon: '🔧', description: 'Book your service' },
            { name: 'Contact Us', href: '/contact', icon: '📞', description: 'Get in touch' }
          ]
        },
        {
          heading: 'Popular Articles',
          icon: FileText,
          items: [
            { name: 'Porsche PSM Failure Guide', href: '/blog/porsche-psm-failure-guide', new: true, description: 'Common PSM issues' },
            { name: 'VW EPC Light Fix', href: '/blog/fix-vw-epc-light', new: true, description: 'EPC troubleshooting' }
          ]
        },
        {
          heading: 'Company',
          icon: Info,
          items: [
            { name: 'About Us', href: '/about', description: 'Our story' },
            { name: 'Contact', href: '/contact', description: 'Get in touch' },
            { name: 'Reviews', href: '/about#reviews', description: '5-star service' },
            { name: 'Warranty', href: '/warranty', description: 'Service guarantee' }
          ]
        }
      ]
    }
  };

  return (
    <nav className="relative">
      <div className="flex items-center space-x-8">
        {Object.entries(megaMenuData).map(([key, menu]) => (
          <div
            key={key}
            className="relative"
            onMouseEnter={() => handleMouseEnter(key)}
            onMouseLeave={handleMouseLeave}
          >
            <button
              className={`flex items-center space-x-1 py-2 px-3 text-gray-700 hover:text-primary-600 transition-colors font-medium ${activeMenu === key ? 'text-primary-600' : ''
                }`}
            >
              <span>{menu.title}</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${activeMenu === key ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence>
              {activeMenu === key && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.2 }}
                  className="absolute mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 z-50"
                  style={{
                    ...(key === 'resources' ? { right: 0 } : { left: 0 }),
                    width: 'max-content',
                    maxWidth: key === 'vehicles' ? '500px' : key === 'resources' ? '580px' : '700px'
                  }}
                  onMouseEnter={() => handleMouseEnter(key)}
                  onMouseLeave={handleMouseLeave}
                >
                  <div className="p-4 sm:p-6 max-h-[75vh] overflow-y-auto overflow-x-hidden">
                    <div className="grid gap-4 sm:gap-6" style={{
                      gridTemplateColumns: `repeat(${Math.min(menu.sections.length, 4)}, minmax(0, 1fr))`
                    }}>
                      {menu.sections.map((section, idx) => (
                        <div key={idx} className={section.featured ? 'col-span-1' : 'col-span-1'}>
                          {section.featured ? (
                            <>
                              <h3 className="font-bold text-gray-900 mb-3">{section.heading}</h3>
                              {section.content}
                            </>
                          ) : section.content ? (
                            <>
                              {section.heading && (
                                <h3 className="font-bold text-gray-900 mb-3 flex items-center">
                                  {section.icon && <section.icon className="w-4 h-4 mr-2 text-primary-600" />}
                                  {section.heading}
                                </h3>
                              )}
                              {section.content}
                            </>
                          ) : (
                            <>
                              <h3 className="font-bold text-gray-900 mb-3 flex items-center">
                                {section.icon && <section.icon className="w-4 h-4 mr-2 text-primary-600" />}
                                {section.heading}
                              </h3>
                              <ul className="space-y-2">
                                {section.items?.map((item, itemIdx) => (
                                  <li key={itemIdx}>
                                    <Link
                                      to={item.href}
                                      className="group flex items-start space-x-2 text-sm hover:text-primary-600 transition-colors"
                                      onClick={() => setActiveMenu(null)}
                                    >
                                      {item.icon && <span className="mt-0.5">{item.icon}</span>}
                                      <div className="flex-1">
                                        <div className="flex items-center space-x-2">
                                          <span className="font-medium group-hover:underline">{item.name}</span>
                                          {item.hot && (
                                            <span className="bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded-full font-semibold">
                                              HOT
                                            </span>
                                          )}
                                          {item.new && (
                                            <span className="bg-primary-100 text-primary-700 text-xs px-2 py-0.5 rounded-full font-semibold">
                                              NEW
                                            </span>
                                          )}
                                          {item.badge && (
                                            <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full font-semibold">
                                              {item.badge}
                                            </span>
                                          )}
                                        </div>
                                        {item.description && (
                                          <p className="text-xs text-gray-500 mt-0.5">{item.description}</p>
                                        )}
                                      </div>
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Bottom bar with CTA */}
                  <div className="bg-gray-50 px-4 sm:px-6 py-3 border-t border-gray-200 rounded-b-xl">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                      <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
                        <div className="flex items-center text-sm text-gray-600">
                          <Phone className="w-4 h-4 mr-1" />
                          <span>Call: </span>
                          <a href="tel:8173822316" className="font-semibold text-gray-900 hover:text-primary-600 ml-1">
                            (817) 382-2316
                          </a>
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <Clock className="w-4 h-4 mr-1" />
                          <span>Mon-Fri 8am-5PM</span>
                        </div>
                      </div>
                      {key === 'services' && (
                        <Link
                          to="/service"
                          className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors text-sm font-semibold whitespace-nowrap"
                          onClick={() => setActiveMenu(null)}
                        >
                          Book Service →
                        </Link>
                      )}
                      {key === 'vehicles' && (
                        <Link
                          to="/vehicles"
                          className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors text-sm font-semibold whitespace-nowrap"
                          onClick={() => setActiveMenu(null)}
                        >
                          View All Inventory →
                        </Link>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}

        {/* Regular nav items without dropdown */}
        <Link
          to="/about"
          className="py-2 px-3 text-gray-700 hover:text-primary-600 transition-colors font-medium"
        >
          About
        </Link>
        <Link
          to="/contact"
          className="py-2 px-3 text-gray-700 hover:text-primary-600 transition-colors font-medium"
        >
          Contact
        </Link>

        {/* CTA Button */}
        <button
          onClick={onBookService}
          className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors font-semibold flex items-center space-x-2"
        >
          <Calendar className="w-4 h-4" />
          <span>Book Service</span>
        </button>
      </div>
    </nav>
  );
};

export default MegaMenu;
